import React, { useCallback } from 'react';
import { motion } from 'framer-motion';
import { useProcessing } from '../../contexts/ProcessingContext';
import { useNotification } from '../../contexts/NotificationContext';
import { assetsApi } from '../../lib/assetsApi';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiUpload, FiFile, FiX } = FiIcons;

export default function AssetUploader() {
  const { uploadedFiles, addUploadedFile, setCurrentStep, setProcessing } = useProcessing();
  const { showSuccess, showError } = useNotification();

  const handleFileUpload = useCallback(async (files) => {
    setProcessing(true);
    
    try {
      for (const file of files) {
        const uploadedAsset = await assetsApi.uploadAsset(file, 'document');
        addUploadedFile({
          id: uploadedAsset.id,
          name: file.name,
          size: file.size,
          type: file.type,
          url: uploadedAsset.url,
          originalFile: file
        });
      }
      
      showSuccess(`${files.length} file(s) uploaded successfully`);
    } catch (error) {
      showError('Failed to upload files: ' + error.message);
    } finally {
      setProcessing(false);
    }
  }, [addUploadedFile, showSuccess, showError, setProcessing]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    handleFileUpload(files);
  }, [handleFileUpload]);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    handleFileUpload(files);
  };

  const handleProcessFiles = () => {
    if (uploadedFiles.length > 0) {
      setCurrentStep('process');
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center hover:border-gray-400 transition-colors"
        onDrop={handleDrop}
        onDragOver={(e) => e.preventDefault()}
        whileHover={{ scale: 1.02 }}
      >
        <SafeIcon icon={FiUpload} className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Upload Documents
        </h3>
        <p className="text-gray-600 mb-4">
          Drag and drop your files here, or click to browse
        </p>
        <input
          type="file"
          multiple
          onChange={handleFileSelect}
          className="hidden"
          id="file-upload"
          accept=".pdf,.doc,.docx,.ppt,.pptx,.jpg,.jpeg,.png"
        />
        <label
          htmlFor="file-upload"
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
        >
          <SafeIcon icon={FiUpload} className="w-4 h-4 mr-2" />
          Browse Files
        </label>
      </motion.div>

      {uploadedFiles.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Uploaded Files ({uploadedFiles.length})
          </h3>
          
          <div className="space-y-3 mb-6">
            {uploadedFiles.map((file, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex items-center">
                  <SafeIcon icon={FiFile} className="w-5 h-5 text-gray-500 mr-3" />
                  <div>
                    <p className="font-medium text-gray-900">{file.name}</p>
                    <p className="text-sm text-gray-500">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-red-600">
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>
            ))}
          </div>

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleProcessFiles}
            className="w-full py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium"
          >
            Process Files
          </motion.button>
        </motion.div>
      )}
    </div>
  );
}